// Diário › Hábitos (0.65): nome, ícone e frequência (todo dia, dias da semana, X vezes por semana, personalizado),
// marcar "feito" com um toque, calendário de bolinhas do mês, ligação com uma meta e com o Bem-Estar
// (marcado sozinho, sem marcar duas vezes) e "Concluído" = hábito formado (vai para o Mural).
import { useEffect, useState } from 'react';
import { Alert, Pressable, Text, View } from 'react-native';
import { useAllDocs } from '../../store/store';
import { confDiario, gravarHabitos, lerHabitos, lerMetas, novoHabito, useDiario, type FreqHabito, type Habito, abrirDaLixeira, selarParaLixeira } from '../../store/diario';
import type { Docs } from '../../store/types';
import { AcoesItem } from '../Acoes';
import { Ordenavel } from '../Arrastar';
import { Arquivados, useSelecao } from '../Selecao';
import { EmojiPicker } from '../EmojiPicker';
import { Btn, Chip, Field, Screen, Sheet } from '../ui';
import { C, alpha } from '../theme';
import { CalendarioMes, Corpo, Recolhe, dataLonga, tarefaDe, useEnviarDiario } from './comum';
import { LIGACOES_BE, cumpridosNoMes, feitoEm, feitoNoBemEstar, planejado, seguidos, textoFreq } from '../../lib/habitos';
import { useOrganizar } from '../../lib/organizar';
import { paraLixeira, registrarRestauro } from '../../lib/lixeira';
import { diaCurto, today, uid } from '../../lib/date';
import { t } from '../../lib/i18n';
import { useBotaoNovo } from '../../lib/botaoNovo';
import { useCaixas } from '../../lib/caixas';

registrarRestauro('diario-habito', (c: unknown) => { const h = abrirDaLixeira<Habito>(c); if (!lerHabitos().some(x => x.id === h.id)) gravarHabitos([...lerHabitos(), h]); });

/** "🔥 N dias seguidos" ou "N dias cumpridos no mês" (escolha em Ajustes › Abas › Diário). */
export function textoSequencia(h: Habito, docs: Docs) {
  const tk = tarefaDe('hab:' + h.id);
  return confDiario().seq === 'mes'
    ? t('{n} dia(s) cumprido(s) no mês', { n: cumpridosNoMes(h, docs, tk) })
    : '🔥 ' + t('{n} dia(s) seguido(s)', { n: seguidos(h, docs, tk) });
}

export function alternarHoje(h: Habito, data = today()) {
  const l = lerHabitos();
  gravarHabitos(l.map(x => (x.id === h.id ? { ...x, feitos: x.feitos.includes(data) ? x.feitos.filter(d => d !== data) : [...x.feitos, data] } : x)));
}

export function Habitos({ cor, abrirId, onBack }: { cor: string; abrirId?: string | null; onBack: () => void }) {
  useDiario();
  const docs = useAllDocs();
  const habitos = lerHabitos();
  const [ed, setEd] = useState<Habito | null>(null);
  useBotaoNovo({ rotulo: t('Novo hábito'), fn: () => setEd(novoHabito()) });   // 11d F
  const cx = useCaixas();   // "Tamanho das caixas" (Grande = como antes)
  const org = useOrganizar('diario-habitos');
  const enviarD = useEnviarDiario();
  useEffect(() => { if (abrirId) { const h = habitos.find(x => x.id === abrirId); if (h) setEd(h); } }, [abrirId]); // eslint-disable-line react-hooks/exhaustive-deps
  const hoje = today();
  const ativosTodos = habitos.filter(h => !h.formado);
  const { ativos, arquivados } = org.separar([...ativosTodos].sort((a, b) => (a.ordem ?? 1e9) - (b.ordem ?? 1e9)), h => h.id);
  const formados = habitos.filter(h => h.formado);
  const excluir = (h: Habito) => { paraLixeira('diario', 'diario-habito', t('Hábito do Diário'), selarParaLixeira(h)); gravarHabitos(lerHabitos().filter(x => x.id !== h.id)); };
  const sel = useSelecao({ itens: ativos, chave: h => h.id, cor, onArquivar: ids => org.arquivar(ids), onExcluir: ids => ids.forEach(id => { const h = habitos.find(x => x.id === id); if (h) excluir(h); }) });
  const salvar = (h: Habito) => gravarHabitos(lerHabitos().some(x => x.id === h.id) ? lerHabitos().map(x => (x.id === h.id ? h : x)) : [...lerHabitos(), h]);

  const cartao = (h: Habito) => {
    const tk = tarefaDe('hab:' + h.id);
    const feito = feitoEm(h, hoje, docs, tk);
    const peloBE = feitoNoBemEstar(h.be, hoje, docs);
    const plan = planejado(h.freq, hoje);
    return (
      <Pressable onPress={() => setEd(h)} style={{ backgroundColor: C.card, borderRadius: cx.raio(16), ...cx.pads(12), gap: cx.gap(8), borderWidth: 1, borderColor: feito ? cor : C.line }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingRight: 22 }}>
          <Pressable disabled={peloBE} onPress={() => alternarHoje(h)} hitSlop={8} accessibilityRole="checkbox" accessibilityState={{ checked: feito }}
            style={{ width: cx.v(40, 36, 30), height: cx.v(40, 36, 30), borderRadius: cx.v(20, 18, 15), borderWidth: 2, borderColor: feito ? cor : C.mute, backgroundColor: feito ? cor : 'transparent', alignItems: 'center', justifyContent: 'center', opacity: plan || feito ? 1 : 0.5 }}>
            <Text style={{ fontSize: cx.icone(feito ? 18 : 20), color: '#fff', fontWeight: '900' }}>{feito ? '✓' : h.icone}</Text>
          </Pressable>
          <View style={{ flex: 1 }}>
            <Text style={{ color: C.text, fontWeight: '700', fontSize: 15 }} numberOfLines={2}>{h.icone} {h.nome || t('Sem nome')}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }}>{[textoFreq(h.freq), !plan ? t('hoje não está planejado') : '', peloBE ? '✓ ' + t('pelo Bem-Estar') : ''].filter(Boolean).join(' · ')}</Text>
          </View>
        </View>
        <Text style={{ color: cor, fontWeight: '700', fontSize: 12 }}>{textoSequencia(h, docs)}</Text>
        <Bolinhas h={h} docs={docs} cor={cor} />
      </Pressable>
    );
  };

  return (
    <Screen title={t('Hábitos')} onBack={onBack}>
      <Text style={{ color: C.sub, fontSize: 13 }}>{t('Toque no círculo para marcar o que fez hoje. Os hábitos não vão para o My Planner sozinhos: use Enviar, no menu ⋮.')}</Text>
      {sel.barra}
      <Ordenavel itens={ativos} chave={h => h.id} selecao={sel.prop}
        onOrdem={l => { const pos = new Map(l.map((h, i) => [h.id, i])); gravarHabitos(lerHabitos().map(h => (pos.has(h.id) ? { ...h, ordem: pos.get(h.id) } : h))); }}
        render={h => (
          <AcoesItem lembrar={{ aba: 'diario', id: h.id, titulo: t('📔 Diário'), item: 'hab:' + h.id }} nome={h.nome} onExcluir={() => excluir(h)} onArquivar={() => org.arquivar(h.id)}
            onDuplicar={() => salvar({ ...JSON.parse(JSON.stringify(h)), id: uid(), nome: h.nome + ' ' + t('(cópia)'), feitos: [], formado: null })}
            menuExtra={[{ label: '📤 ' + t('Enviar para outra aba'), onPress: () => enviarD('hab:' + h.id, h.nome, h.icone) }]}
            fixar={{ on: org.fixado(h.id), alternar: () => org.alternarFixo(h.id) }}>
            {cartao(h)}
          </AcoesItem>
        )} />
      {!ativosTodos.length && <Text style={{ color: C.sub }}>{t('Nenhum hábito ainda. Ex.: "Comer uma fruta por dia", "Beber 2 L de água", "Não pular o café da manhã".')}</Text>}
      <Arquivados itens={arquivados} chave={h => h.id} cor={cor} render={h => <Text style={{ color: C.text }}>{h.icone} {h.nome}</Text>}
        onRestaurar={h => org.restaurar(h.id)} onExcluir={h => Alert.alert(t('Excluir {nome}?', { nome: h.nome }), t('Fica 30 dias na lixeira.'), [{ text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: () => excluir(h) }])} />
      {!!formados.length && (
        <Recolhe titulo={`✨ ${t('Hábitos formados')} (${formados.length})`}>
          {formados.map(h => (
            <Pressable key={h.id} onPress={() => setEd(h)} style={{ backgroundColor: C.card, borderRadius: cx.raio(14), padding: cx.pad(10), flexDirection: 'row', gap: 8 }}>
              <Text style={{ color: C.text, flex: 1 }}>{h.icone} {h.nome}</Text>
              <Text style={{ color: C.sub, fontSize: 12 }}>✨ {dataLonga(h.formado!)}</Text>
            </Pressable>
          ))}
        </Recolhe>
      )}
      <Pressable onPress={() => setEd(novoHabito())} style={{ backgroundColor: cor, borderRadius: 14, padding: 14, alignItems: 'center', marginTop: 6 }}>
        <Text style={{ color: '#fff', fontWeight: '800' }}>+ {t('Novo hábito')}</Text>
      </Pressable>
      <View style={{ height: 60 }} />
      <HabitoSheet hab={ed} cor={cor} onFechar={() => setEd(null)} onSalvar={salvar} onExcluir={h => { excluir(h); setEd(null); }} onEnviar={h => enviarD('hab:' + h.id, h.nome, h.icone)} />
    </Screen>
  );
}

/** Bolinhas do mês: dias cumpridos preenchidos. */
function Bolinhas({ h, docs, cor }: { h: Habito; docs: Docs; cor: string }) {
  const hoje = today();
  const ini = hoje.slice(0, 8) + '01';
  const n = new Date(Number(hoje.slice(0, 4)), Number(hoje.slice(5, 7)), 0).getDate();
  const tk = tarefaDe('hab:' + h.id);
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4 }}>
      {Array.from({ length: n }, (_, i) => {
        const d = ini.slice(0, 8) + String(i + 1).padStart(2, '0');
        const feito = d <= hoje && feitoEm(h, d, docs, tk);
        const plan = planejado(h.freq, d);
        return <View key={d} style={{ width: 9, height: 9, borderRadius: 5, backgroundColor: feito ? cor : d <= hoje && plan ? alpha(cor, 0.2) : C.card2, borderWidth: d === hoje ? 1 : 0, borderColor: cor }} />;
      })}
    </View>
  );
}

export function HabitoSheet({ hab, cor, onFechar, onSalvar, onExcluir, onEnviar }: { hab: Habito | null; cor: string; onFechar: () => void; onSalvar: (h: Habito) => void; onExcluir: (h: Habito) => void; onEnviar: (h: Habito) => void }) {
  const docs = useAllDocs();
  const [h, setH] = useState<Habito | null>(hab);
  const [emoji, setEmoji] = useState(false);
  const [aberto, setAberto] = useState<string | null>(null);
  if ((hab?.id ?? null) !== aberto) { setAberto(hab?.id ?? null); setH(hab); }
  if (!h) return <Sheet visible={false} onClose={onFechar} title=""><View /></Sheet>;
  const set = (p: Partial<Habito>) => setH(x => (x ? { ...x, ...p } : x));
  const setF = (f: FreqHabito) => set({ freq: f });
  const fechar = () => { if (h.nome.trim()) onSalvar({ ...h, nome: h.nome.trim() }); onFechar(); };
  const metas = lerMetas().filter(m => !m.concluida);
  const tk = tarefaDe('hab:' + h.id);
  return (
    <Sheet visible={!!hab} onClose={fechar} title={t('Hábito')} right={<Pressable onPress={fechar}><Text style={{ color: cor, fontWeight: '800' }}>{t('Pronto')}</Text></Pressable>}>
      <Corpo>
        <View style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}>
          <Pressable onPress={() => setEmoji(true)} style={{ width: 52, height: 52, borderRadius: 14, backgroundColor: C.card, alignItems: 'center', justifyContent: 'center' }}><Text style={{ fontSize: 28 }}>{h.icone}</Text></Pressable>
          <Field value={h.nome} onChangeText={v => set({ nome: v })} placeholder={t('Ex.: Comer uma fruta por dia')} style={{ flex: 1 }} autoFocus={!h.nome} />
        </View>
        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>{t('FREQUÊNCIA')}</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
          <Chip small cor={cor} on={h.freq.tipo === 'dia'} label={t('Todo dia')} onPress={() => setF({ tipo: 'dia' })} />
          <Chip small cor={cor} on={h.freq.tipo === 'dias'} label={t('Dias da semana')} onPress={() => setF({ tipo: 'dias', dias: [1, 2, 3, 4, 5] })} />
          <Chip small cor={cor} on={h.freq.tipo === 'vezes'} label={t('X vezes por semana')} onPress={() => setF({ tipo: 'vezes', n: 3 })} />
          <Chip small cor={cor} on={h.freq.tipo === 'cada'} label={t('Personalizado')} onPress={() => setF({ tipo: 'cada', n: 2, desde: today() })} />
        </View>
        {h.freq.tipo === 'dias' && (
          <View style={{ flexDirection: 'row', gap: 6 }}>
            {[0, 1, 2, 3, 4, 5, 6].map(d => {
              const f = h.freq as { tipo: 'dias'; dias: number[] };
              const on = f.dias.includes(d);
              return <Chip key={d} small cor={cor} on={on} label={diaCurto(d)} onPress={() => setF({ tipo: 'dias', dias: on ? f.dias.filter(x => x !== d) : [...f.dias, d] })} />;
            })}
          </View>
        )}
        {(h.freq.tipo === 'vezes' || h.freq.tipo === 'cada') && (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <Pressable onPress={() => setF({ ...(h.freq as any), n: Math.max(1, (h.freq as any).n - 1) })} style={{ width: 38, height: 38, borderRadius: 19, backgroundColor: C.card2, alignItems: 'center', justifyContent: 'center' }}><Text style={{ color: C.text, fontSize: 20 }}>−</Text></Pressable>
            <Text style={{ color: C.text, fontWeight: '800', fontSize: 16 }}>{h.freq.tipo === 'vezes' ? t('{n} vez(es) por semana', { n: h.freq.n }) : t('A cada {n} dias', { n: h.freq.n })}</Text>
            <Pressable onPress={() => setF({ ...(h.freq as any), n: Math.min(h.freq.tipo === 'vezes' ? 7 : 60, (h.freq as any).n + 1) })} style={{ width: 38, height: 38, borderRadius: 19, backgroundColor: C.card2, alignItems: 'center', justifyContent: 'center' }}><Text style={{ color: C.text, fontSize: 20 }}>+</Text></Pressable>
          </View>
        )}
        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800', marginTop: 6 }}>{t('META OU SONHO LIGADO (OPCIONAL)')}</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
          <Chip small cor={cor} on={!h.meta} label={t('Nenhum')} onPress={() => set({ meta: null })} />
          {metas.map(m => <Chip key={m.id} small cor={cor} on={h.meta === m.id} label={`${m.icone} ${m.titulo}`} onPress={() => set({ meta: m.id })} />)}
        </View>
        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800', marginTop: 6 }}>{t('MARCAR SOZINHO PELO BEM-ESTAR (OPCIONAL)')}</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
          <Chip small cor={cor} on={!h.be} label={t('Não')} onPress={() => set({ be: null })} />
          {LIGACOES_BE.map(([k, n]) => <Chip key={k} small cor={cor} on={h.be === k} label={t(n)} onPress={() => set({ be: k })} />)}
        </View>
        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800', marginTop: 6 }}>{t('DIAS CUMPRIDOS')}</Text>
        <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 10 }}>
          <CalendarioMes data={today()} cor={cor} pequeno marcados={d => d <= today() && feitoEm(h, d, docs, tk)}
            onDia={d => { if (d > today()) return; set({ feitos: h.feitos.includes(d) ? h.feitos.filter(x => x !== d) : [...h.feitos, d] }); }} />
          <Text style={{ color: C.sub, fontSize: 11 }}>{t('Toque num dia para marcar ou desmarcar.')}</Text>
        </View>
        <View style={{ gap: 8, marginTop: 10 }}>
          {h.formado
            ? <Btn cor={cor} outline label={'↩︎ ' + t('Voltar a acompanhar este hábito')} onPress={() => set({ formado: null })} />
            : <Btn cor={cor} label={'✨ ' + t('Concluído: hábito formado')} onPress={() => { onSalvar({ ...h, formado: today() }); onFechar(); Alert.alert('🎉 ' + t('Parabéns!'), t('"{nome}" já faz parte da sua rotina. Foi para o Mural, em Conquistas.', { nome: h.nome })); }} />}
          <Btn cor={cor} outline label={'📤 ' + t('Enviar o título para outra aba')} onPress={() => { onSalvar(h); onEnviar(h); }} />
          <Pressable onPress={() => Alert.alert(t('Excluir {nome}?', { nome: h.nome || t('este item') }), t('Fica 30 dias na lixeira.'), [{ text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: () => onExcluir(h) }])} style={{ padding: 10, alignItems: 'center' }}>
            <Text style={{ color: C.danger }}>🗑 {t('Excluir')}</Text>
          </Pressable>
        </View>
      </Corpo>
      <EmojiPicker visible={emoji} cor={cor} onClose={() => setEmoji(false)} onPick={e => set({ icone: e })} />
    </Sheet>
  );
}
